<template>
  <div class="relative h-[295px] rounded-xl" ref="containerRef">
    <!-- card background -->
    <div class="absolute inset-0 rounded-xl border border-blue-900/40 overflow-hidden" style="background: #080e1c;" />

    <!-- globe positioned right-center like the design system -->
    <div class="absolute inset-0 overflow-hidden rounded-xl">
      <div class="absolute" style="top: 50%; left: 50%; transform: translate(-40%, -52%)">
        <WorldGlobe
          :regions="regions"
          :model-value="modelValue"
          :size="350"
          :disabled="disabled"
          @update:model-value="(r) => $emit('update:modelValue', r)"
        />
      </div>
    </div>

    <!-- bottom gradient -->
    <div
      class="absolute inset-x-0 bottom-0 h-28 rounded-b-xl"
      style="background: linear-gradient(to top, #080e1c 40%, rgba(8,14,28,0.6) 75%, transparent)" />

    <!-- footer toggle — same pattern as MapsDropdown -->
    <div class="absolute bottom-2 left-2 right-2 z-50">
      <button
        @click="toggleOpen"
        :disabled="disabled"
        class="w-full flex items-center justify-between rounded-md backdrop-blur-[1.5px] px-4 py-3 "
        :class="disabled ? 'cursor-not-allowed' : ''"
      >
        <div class="flex flex-col gap-1">
          <span class="text-xs uppercase tracking-wide text-gray-400 font-semibold text-left">{{ $t('play.matchmaking.region') }}</span>
          <span class="font-semibold text-base text-left" :class="disabled ? 'text-gray-400' : 'text-text-primary'">{{ modelValue?.name || $t('play.matchmaking.select_region') }}</span>
        </div>
        <LockClosedIcon v-if="disabled" class="h-4 w-4 text-gray-500 flex-none" aria-hidden="true" />
        <ChevronDownIcon v-else class="h-5 w-5 text-gray-400 flex-none transition-transform" :class="open ? 'rotate-180' : ''" />
      </button>

      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-if="open"
          class="absolute bottom-full left-0 mb-2 w-full origin-bottom rounded-lg border overflow-hidden"
          style="background: #0F141A; border-color: #1E2733; box-shadow: 0px 8px 32px rgba(0,0,0,0.16);"
        >
          <div class="overflow-y-auto max-h-64 py-2">
            <button
              v-for="region in regions"
              :key="region.id"
              @click.stop="select(region)"
              :disabled="disabled"
              class="w-full flex items-center px-2 py-2 gap-3 h-9 rounded transition-colors text-left hover:bg-white/5"
            >
              <!-- radio indicator -->
              <span
                class="flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors"
                :style="modelValue?.id === region.id
                  ? 'border-color: #6366f1; background: #6366f1'
                  : 'border-color: #374151; background: transparent'"
              >
                <span v-if="modelValue?.id === region.id" class="w-1.5 h-1.5 rounded-full bg-white" />
              </span>
              <span
                class="text-sm font-medium"
                :style="modelValue?.id === region.id ? 'color: #EFF2F5' : 'color: #9CADC4'"
              >{{ region.name }}</span>
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import {
  ref, watch, onMounted, onUnmounted,
} from 'vue';
import { ChevronDownIcon, LockClosedIcon } from '@heroicons/vue/24/solid';
import WorldGlobe from './WorldGlobe.vue';

const props = defineProps({
  regions: { type: Array, default: () => [] },
  modelValue: { type: Object, default: null },
  // Locked while the party is queued, and for everyone who is not the leader.
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const open = ref(false);
const containerRef = ref(null);

function toggleOpen() {
  if (props.disabled) return;
  open.value = !open.value;
}

function select(region) {
  if (props.disabled) return;
  emit('update:modelValue', region);
  open.value = false;
}

function onClickOutside(e) {
  if (containerRef.value && !containerRef.value.contains(e.target)) open.value = false;
}

// Queueing can lock the panel while it is open — collapse it rather than
// leaving an open list the leader can no longer act on.
watch(() => props.disabled, (locked) => {
  if (locked) open.value = false;
});

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', onClickOutside));
</script>
